// Pestaña "Directivas" (cerebro, etapa 3): sección "Reglas que el cerebro propone" y cuaderno de
// reglas vigente del ayudante de preguntas. El cerebro solo PROPONE reglas a partir de lo que el
// dueño corrigió; nada entra al cuaderno ni a las directivas sin el sí del dueño (Sí / Sí con
// cambios / No). Solo el Administrador (el servidor lo exige). Sin dinero en pantalla.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Lightbulb, BookOpen, Loader2, RefreshCw } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';
import { fetchResult } from './brainView';
import { confirmClickAllowed } from './decisionsView';
import {
  APPROVE_FAIL,
  EVIDENCE_NOTE,
  PROFILE_LABEL,
  REJECT_FAIL,
  REJECT_OPTIONS,
  RULE_TEXT_MAX,
  STRIKE_FAIL,
  categoryLabel,
  editProblem,
  evidenceLines,
  groupFaq,
  nextRules,
  profileLabel,
  ruleTextToSend,
  scopeLabel,
  serverText,
  type FaqItem,
} from './rulesView';
import { fmtDateTime } from './shared';

interface RuleRow {
  id: number;
  seller_id: number | null;
  seller_name: string | null;
  profile: string;
  category: string;
  text: string;
  evidence: unknown;
  created_at: string;
}
export interface RulesData {
  rows: RuleRow[];
  total: number;
  counts: { candidata: number; aprobada: number; rechazada: number };
  faq: FaqItem[];
}
interface Notice {
  id: number;
  ok: boolean;
  text: string;
}

const REFRESH_MS = 60_000;
/** Vence sola la confirmación del "Sí". */
const CONFIRM_MS = 10_000;
const LIMIT = 50;

const btnGo =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';
const btnGhost =
  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] disabled:opacity-40';
const btnNo =
  'inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--color-error)] text-[var(--color-error)] hover:opacity-80 disabled:opacity-40';
const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const errBox =
  'rounded-lg p-2 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]';
const okBox =
  'rounded-lg p-2 text-sm bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]';

/** A dónde va la regla aprobada, en palabras del dueño. */
const destination = (profile: string) =>
  profile === 'post_sale'
    ? 'las directivas del asistente, marcada para los mensajes posventa a compradores (el asistente la ve en todas sus tareas)'
    : 'el cuaderno de reglas del ayudante de preguntas';

export default function RulesProposed({
  reloadNonce,
  onData,
  onDecided,
}: {
  /** Al cambiar, se vuelve a leer (p. ej. después de tachar una entrada del cuaderno). */
  reloadNonce: number;
  /** Cada lectura: datos (null si nunca se pudo leer) para el cuaderno vigente de abajo. */
  onData: (d: RulesData | null, failed: boolean) => void;
  /** Una regla se aprobó o rechazó: directivas y contador de la pestaña al día. */
  onDecided: () => void;
}) {
  const sellers = useUIStore((s) => s.sellers);
  const [data, setData] = useState<RulesData | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [seller, setSeller] = useState('');
  const [profile, setProfile] = useState('');
  const [notices, setNotices] = useState<Notice[]>([]);
  const [gone, setGone] = useState<Set<number>>(new Set());
  const dataRef = useRef<RulesData | null>(null);
  const seq = useRef(0);
  const noticeSeq = useRef(0);
  // Tarjetas en edición, confirmando o enviando: mientras haya alguna no se auto-refresca.
  const editing = useRef(new Set<number>());
  // Reglas con un pedido en curso: nunca salen dos pedidos por la misma.
  const inFlight = useRef(new Set<number>());

  const load = useCallback(async () => {
    const my = ++seq.current;
    setLoading(true);
    const q = new URLSearchParams({ status: 'candidata', limit: String(LIMIT) });
    if (seller) q.set('seller_id', seller);
    if (profile) q.set('profile', profile);
    const f = await fetchResult(() => api.get<RulesData>(`/assistant/rules?${q.toString()}`));
    if (my !== seq.current) return;
    const n = nextRules(dataRef.current, f);
    dataRef.current = n.value;
    setData(n.value);
    setWarning(n.warning);
    setLoading(false);
    if (f.kind === 'ok') setGone(new Set());
    onData(n.value, f.kind !== 'ok');
  }, [seller, profile, onData]);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (editing.current.size === 0) void load();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [load, reloadNonce]);

  const setEditing = useCallback((id: number, on: boolean) => {
    if (on) editing.current.add(id);
    else editing.current.delete(id);
  }, []);

  const finished = useCallback(
    (id: number, ok: boolean, text: string) => {
      editing.current.delete(id);
      setGone((g) => new Set(g).add(id));
      const nid = ++noticeSeq.current;
      setNotices((ns) => [{ id: nid, ok, text }, ...ns].slice(0, 5));
      onDecided();
      void load();
    },
    [load, onDecided]
  );

  const rows = (data?.rows ?? []).filter((r) => !gone.has(r.id));
  const count = data ? Math.max(0, data.counts.candidata - gone.size) : null;

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-semibold text-[var(--color-text)]">
            <Lightbulb size={16} className="text-[var(--color-primary)]" />
            Reglas que el cerebro propone{count !== null ? ` (${count})` : ''}
          </div>
          <p className="text-xs text-[var(--color-text-secondary)] mt-1">
            Salen de las respuestas que corregiste. Ninguna se usa hasta que digas que sí: las de
            preguntas entran al cuaderno del ayudante de preguntas y las de mensajes, a las
            directivas.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1 text-[var(--color-text-secondary)]">
            Empresa
            <select value={seller} onChange={(e) => setSeller(e.target.value)} className={inputCls}>
              <option value="">Todas</option>
              {sellers.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.nickname || `Empresa ${s.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-[var(--color-text-secondary)]">
            Tipo
            <select value={profile} onChange={(e) => setProfile(e.target.value)} className={inputCls}>
              <option value="">Todos</option>
              {Object.entries(PROFILE_LABEL).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void load()}
            title="Actualizar"
            className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {warning && <div className={errBox}>{warning}</div>}
      {notices.map((n) => (
        <div key={n.id} className={n.ok ? okBox : errBox}>
          {n.text}
        </div>
      ))}

      {loading && !data && !warning ? (
        <div className="flex items-center justify-center py-6 text-[var(--color-text-secondary)]">
          <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
        </div>
      ) : data && rows.length === 0 && !warning ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          No hay reglas propuestas esperando tu decisión.
        </p>
      ) : null}
      {data && data.total > data.rows.length && (
        <p className="text-xs text-[var(--color-text-muted)]">
          Se muestran {data.rows.length} de {data.total}.
        </p>
      )}

      <ul className="space-y-3">
        {rows.map((r) => (
          <RuleCard
            key={r.id}
            row={r}
            setEditing={setEditing}
            finished={finished}
            inFlight={inFlight.current}
            refresh={() => void load()}
          />
        ))}
      </ul>
    </div>
  );
}

type Mode = 'idle' | 'confirm' | 'edit' | 'edit_confirm' | 'reject' | 'busy';

function RuleCard({
  row,
  setEditing,
  finished,
  inFlight,
  refresh,
}: {
  row: RuleRow;
  setEditing: (id: number, on: boolean) => void;
  finished: (id: number, ok: boolean, text: string) => void;
  inFlight: Set<number>;
  refresh: () => void;
}) {
  const [mode, setModeState] = useState<Mode>('idle');
  const [draft, setDraft] = useState(row.text);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Hora en que se armó la confirmación: el segundo clic de un doble clic no la dispara.
  const armedAt = useRef(0);

  const setMode = (m: Mode) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setModeState(m);
    setEditing(row.id, m !== 'idle');
  };

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      setEditing(row.id, false);
    },
    [row.id, setEditing]
  );

  /** Primer clic del "Sí": pide confirmar; vence sola a los 10 s. */
  const askConfirm = (m: 'confirm' | 'edit_confirm', back: Mode) => {
    armedAt.current = Date.now();
    setMode(m);
    timer.current = setTimeout(() => {
      timer.current = null;
      setModeState(back);
      setEditing(row.id, back !== 'idle');
    }, CONFIRM_MS);
  };

  const confirmed = (go: () => void) => {
    if (!confirmClickAllowed(armedAt.current, Date.now())) return;
    go();
  };

  const approve = async (edited?: string) => {
    if (inFlight.has(row.id)) return;
    inFlight.add(row.id);
    const text = edited === undefined ? undefined : ruleTextToSend(edited, row.text);
    const back: Mode = edited === undefined ? 'idle' : 'edit';
    setMode('busy');
    setError(null);
    try {
      const r = await fetchResult(() =>
        api.post<{ applied_ref: string }>(
          `/assistant/rules/${row.id}/approve`,
          text === undefined ? {} : { text }
        )
      );
      if (r.kind !== 'ok') {
        setError(serverText(r, APPROVE_FAIL));
        setMode(back);
        // Pudo haberla decidido otro (o haberse aplicado igual): la lista se relee.
        refresh();
        return;
      }
      finished(row.id, true, `✓ Aprobada: entra a ${destination(row.profile)}.`);
    } finally {
      inFlight.delete(row.id);
    }
  };

  const reject = async (reason: string) => {
    if (inFlight.has(row.id)) return;
    inFlight.add(row.id);
    setMode('busy');
    setError(null);
    let r;
    try {
      r = await fetchResult(() =>
        api.post<{ ok: true }>(`/assistant/rules/${row.id}/reject`, { reason })
      );
    } finally {
      inFlight.delete(row.id);
    }
    if (r.kind !== 'ok') {
      setError(serverText(r, REJECT_FAIL));
      setMode('reject');
      refresh();
      return;
    }
    const why = REJECT_OPTIONS.find((o) => o.value === reason)?.label ?? 'Otro';
    finished(row.id, true, `Rechazada (${why}). El cerebro no la vuelve a proponer.`);
  };

  const busy = mode === 'busy';
  const inEdit = mode === 'edit' || mode === 'edit_confirm';
  const problem = inEdit ? editProblem(draft) : null;
  const examples = evidenceLines(row);

  return (
    <li
      className={`rounded-lg border border-[var(--color-border)] p-3 space-y-2 ${busy ? 'pointer-events-none opacity-60' : ''}`}
      aria-busy={busy}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text)]">
          {categoryLabel(row.category)}
        </span>
        <span>·</span>
        <span className="font-medium text-[var(--color-text)]">{scopeLabel(row)}</span>
        <span>·</span>
        <span>{profileLabel(row.profile)}</span>
        <span className="ml-auto">{fmtDateTime(row.created_at)}</span>
      </div>

      <div className="text-base font-medium text-[var(--color-text)] whitespace-pre-wrap break-words">
        {row.text}
      </div>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Por qué
      </button>
      {open && (
        <div className="space-y-1 rounded border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-2">
          {examples.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)]">No hay ejemplos guardados.</p>
          ) : (
            <>
              <ul className="list-disc pl-5 space-y-1 text-xs text-[var(--color-text)]">
                {examples.map((e, i) => (
                  <li key={i} className="whitespace-pre-wrap break-words">
                    {e}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[var(--color-text-muted)]">{EVIDENCE_NOTE}</p>
            </>
          )}
        </div>
      )}

      {inEdit && (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={mode === 'edit_confirm'}
            rows={3}
            className={`${inputCls} w-full resize-y text-sm`}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span
              className={
                draft.trim().length > RULE_TEXT_MAX
                  ? 'text-[var(--color-error)]'
                  : 'text-[var(--color-text-muted)]'
              }
            >
              {draft.trim().length} / {RULE_TEXT_MAX}
            </span>
            {problem && <span className="text-[var(--color-error)]">{problem}</span>}
          </div>
        </div>
      )}

      {error && <div className={errBox}>{error}</div>}

      <Can cap="assistant.decide">
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'idle' && (
            <>
              <button type="button" className={btnGo} onClick={() => askConfirm('confirm', 'idle')}>
                Sí
              </button>
              <button
                type="button"
                className={btnGhost}
                onClick={() => {
                  setDraft(row.text);
                  setMode('edit');
                }}
              >
                Sí con cambios
              </button>
              <button type="button" className={btnNo} onClick={() => setMode('reject')}>
                No
              </button>
            </>
          )}
          {/* La confirmación queda a la derecha y "Cancelar" donde estaba el botón anterior:
              el segundo clic de un doble clic no cae sobre "Sí, aprobar". */}
          {mode === 'confirm' && (
            <>
              <button type="button" className={btnGhost} onClick={() => setMode('idle')}>
                Cancelar
              </button>
              <span className="ml-auto">
                <button
                  type="button"
                  className={btnGo}
                  onClick={() => confirmed(() => void approve())}
                >
                  ¿Seguro? Entra a {destination(row.profile)}. Sí, aprobar
                </button>
              </span>
            </>
          )}
          {mode === 'edit' && (
            <>
              <button
                type="button"
                className={btnGo}
                disabled={!!problem}
                onClick={() => askConfirm('edit_confirm', 'edit')}
              >
                Aprobar así
              </button>
              <button type="button" className={btnGhost} onClick={() => setMode('idle')}>
                Cancelar
              </button>
            </>
          )}
          {mode === 'edit_confirm' && (
            <>
              <button type="button" className={btnGhost} onClick={() => setMode('edit')}>
                Volver a editar
              </button>
              <span className="ml-auto">
                <button
                  type="button"
                  className={btnGo}
                  disabled={!!problem}
                  onClick={() => confirmed(() => void approve(draft))}
                >
                  ¿Seguro? Entra a {destination(row.profile)}. Sí, aprobar
                </button>
              </span>
            </>
          )}
          {mode === 'reject' && (
            <>
              <span className="text-xs text-[var(--color-text-secondary)]">¿Por qué no?</span>
              {REJECT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={btnNo}
                  onClick={() => void reject(o.value)}
                >
                  {o.label}
                </button>
              ))}
              <button type="button" className={btnGhost} onClick={() => setMode('idle')}>
                Cancelar
              </button>
            </>
          )}
          {busy && <span className="text-xs text-[var(--color-text-muted)]">Procesando…</span>}
        </div>
      </Can>
    </li>
  );
}

/**
 * Cuaderno de reglas vigente del ayudante de preguntas (lo que hoy manda sobre sus respuestas).
 * Solo lectura más "Tachar" con confirmación; las tachadas quedan en gris, sin botón.
 */
export function FaqCurrent({
  faq,
  failed,
  onStruck,
}: {
  /** null = todavía no se pudo leer. */
  faq: FaqItem[] | null;
  failed: boolean;
  onStruck: () => void;
}) {
  const groups = groupFaq(faq ?? []);
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 space-y-3">
      <div>
        <div className="flex items-center gap-2 font-semibold text-[var(--color-text)]">
          <BookOpen size={16} className="text-[var(--color-primary)]" />
          Cuaderno de reglas del ayudante de preguntas (vigente)
        </div>
        <p className="text-xs text-[var(--color-text-secondary)] mt-1">
          Lo que hoy manda sobre las respuestas a preguntas. Tachar una entrada hace que el ayudante
          deje de usarla.
        </p>
      </div>
      {faq === null ? (
        failed ? (
          <div className={errBox}>No se pudo leer el cuaderno.</div>
        ) : (
          <div className="flex items-center py-4 text-[var(--color-text-secondary)] text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Cargando…
          </div>
        )
      ) : groups.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)]">El cuaderno está vacío.</p>
      ) : (
        groups.map((g) => (
          <div key={g.key} className="space-y-1">
            <div className="text-xs font-semibold text-[var(--color-text-secondary)]">{g.title}</div>
            <ul className="space-y-1">
              {g.items.map((e) => (
                <FaqEntry key={e.id} entry={e} onStruck={onStruck} />
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

/** Entradas con un "tachar" en curso: nunca salen dos pedidos por la misma. */
const strikeInFlight = new Set<string>();

function FaqEntry({ entry, onStruck }: { entry: FaqItem; onStruck: () => void }) {
  const [mode, setMode] = useState<'idle' | 'confirm' | 'busy'>('idle');
  const [error, setError] = useState<string | null>(null);
  const armedAt = useRef(0);

  const strike = async () => {
    if (!confirmClickAllowed(armedAt.current, Date.now())) return;
    if (strikeInFlight.has(entry.id)) return;
    strikeInFlight.add(entry.id);
    setMode('busy');
    setError(null);
    try {
      const r = await fetchResult(() =>
        api.post<{ ok: true }>(`/assistant/rules/faq/${encodeURIComponent(entry.id)}/strike`, {})
      );
      if (r.kind !== 'ok') setError(serverText(r, STRIKE_FAIL));
      setMode('idle');
      onStruck();
    } finally {
      strikeInFlight.delete(entry.id);
    }
  };

  return (
    <li
      className={`rounded border border-[var(--color-border)] p-2 text-sm ${entry.tachada ? 'opacity-50' : ''}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        {entry.categoria && (
          <span className="px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-xs text-[var(--color-text)]">
            {categoryLabel(entry.categoria)}
          </span>
        )}
        <span
          className={`flex-1 whitespace-pre-wrap break-words text-[var(--color-text)] ${entry.tachada ? 'line-through' : ''}`}
        >
          {entry.text}
        </span>
        {entry.tachada ? (
          <span className="text-xs text-[var(--color-text-muted)]">Tachada</span>
        ) : (
          <Can cap="assistant.decide">
            {mode === 'idle' && (
              <button
                type="button"
                className={btnGhost}
                onClick={() => {
                  armedAt.current = Date.now();
                  setMode('confirm');
                }}
              >
                Tachar
              </button>
            )}
            {mode === 'confirm' && (
              <span className="flex items-center gap-2">
                <button type="button" className={btnGhost} onClick={() => setMode('idle')}>
                  Cancelar
                </button>
                <button type="button" className={btnNo} onClick={() => void strike()}>
                  ¿Tachar? El ayudante deja de usarla. Sí, tachar
                </button>
              </span>
            )}
            {mode === 'busy' && <Loader2 size={14} className="animate-spin" />}
          </Can>
        )}
      </div>
      {error && <div className={`${errBox} mt-1`}>{error}</div>}
    </li>
  );
}
